<template>
  <v-btn
    variant="flat"
    class="toolbar-slip-send-btn"
    size="small"
    :disabled="!isClosed || sending"
    :title="isClosed ? '요양보호사 앱으로 임금명세서를 발송합니다' : '임금 마감 후 발송할 수 있습니다'"
    @click="openDialog"
  >
    <v-icon v-if="sending" size="14" class="mdi-spin">mdi-loading</v-icon>
    <v-icon v-else size="14">mdi-send-outline</v-icon>
    임금명세서발송
  </v-btn>

  <v-dialog v-model="dialogOpen" max-width="480" persistent>
    <div class="wage-slip-broadcast-dialog">
      <LoadingOverlay :show="previewLoading || sending" :text="sending ? '발송 중...' : '대상 확인 중...'" />
      <div class="wage-slip-broadcast-dialog__header">
        <span>임금명세서 발송</span>
        <button
          type="button"
          class="wage-slip-broadcast-dialog__close"
          :disabled="sending"
          aria-label="닫기"
          @click="closeDialog"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>
      <div class="wage-slip-broadcast-dialog__body">
        <p class="wage-slip-broadcast-dialog__history" :class="{ first: isFirstBroadcast }">
          {{ historyText }}
        </p>
        <p class="wage-slip-broadcast-dialog__ask">임금명세서를 보내시겠습니까?</p>
        <p class="wage-slip-broadcast-dialog__lead">
          {{ yearMonthLabel }} 명세서를 소속 요양보호사 앱 채팅으로 보냅니다.
          서류관리함에도 월별로 보관됩니다.
        </p>
        <ul v-if="broadcastHistory.length > 1" class="wage-slip-broadcast-dialog__past">
          <li v-for="(row, idx) in broadcastHistory" :key="`${row.sentAt}-${idx}`">
            {{ formatBroadcastAt(row.sentAt) }}
            <span v-if="row.sentCount"> · {{ row.sentCount }}명</span>
          </li>
        </ul>
        <p v-if="previewError" class="wage-slip-broadcast-dialog__error">{{ previewError }}</p>
        <template v-else-if="preview">
          <p class="wage-slip-broadcast-dialog__sum">
            발송 대상 <strong>{{ preview.matchedCount }}</strong>명
            <template v-if="preview.matchedCount">
              · 선택 <strong>{{ selectedCayIdxs.length }}</strong
              >명
            </template>
            <span v-if="alreadySentCount" class="wage-slip-broadcast-dialog__sum-sent">
              · 발송완료 {{ alreadySentCount }}명
            </span>
            <span v-if="preview.skippedCount"> · 급여 없음 {{ preview.skippedCount }}명</span>
            <span v-if="preview.unmatchedCount" class="wage-slip-broadcast-dialog__sum-alert">
              · 앱 미연결 {{ preview.unmatchedCount }}명
            </span>
          </p>
          <ul
            v-if="preview.matched.length"
            class="wage-slip-broadcast-dialog__list wage-slip-broadcast-dialog__list--check"
          >
            <li v-for="row in preview.matched" :key="row.cayIdx">
              <label class="wage-slip-broadcast-dialog__check">
                <input
                  v-model="selectedCayIdxs"
                  type="checkbox"
                  :value="row.cayIdx"
                  :disabled="sending"
                />
                <strong>{{ row.displayName || '요양보호사' }}</strong>
                <span v-if="row.bizGubun">{{ row.bizGubun }}</span>
                <em
                  v-if="row.alreadySent"
                  class="wage-slip-broadcast-dialog__badge wage-slip-broadcast-dialog__badge--sent"
                >
                  발송완료<template v-if="row.sentAt"> · {{ formatBroadcastAt(row.sentAt) }}</template>
                </em>
                <em v-else class="wage-slip-broadcast-dialog__badge">미발송</em>
              </label>
            </li>
          </ul>
          <p v-else class="wage-slip-broadcast-dialog__empty">
            해당 월 급여가 있는 소속 요양보호사가 없습니다.
          </p>
          <div v-if="preview.unmatched.length" class="wage-slip-broadcast-dialog__unmatched">
            <p class="wage-slip-broadcast-dialog__unmatched-title">
              급여는 있는데 앱에 연결되지 않아 발송에서 빠집니다.
            </p>
            <ul class="wage-slip-broadcast-dialog__unmatched-list">
              <li v-for="(row, idx) in preview.unmatched" :key="`unmatched-${idx}`">
                {{ row.displayName }}<span v-if="row.bizGubun"> · {{ row.bizGubun }}</span>
              </li>
            </ul>
            <p class="wage-slip-broadcast-dialog__unmatched-guide">
              요양보호사가 같은 성명·휴대폰으로 앱에 가입한 뒤, 센터 앱 인사관리에서 「동기화」를 눌러 주세요.
            </p>
          </div>
          <ul v-if="preview.skipped.length" class="wage-slip-broadcast-dialog__skipped">
            <li v-for="(row, idx) in preview.skipped" :key="`skip-${idx}`">
              {{ row.displayName }} · {{ row.reason }}
            </li>
          </ul>
        </template>
      </div>
      <div class="wage-slip-broadcast-dialog__footer">
        <v-btn variant="text" class="btn-close" :disabled="sending" @click="closeDialog">취소</v-btn>
        <v-btn
          variant="flat"
          class="btn-save"
          :disabled="sending || previewLoading || !canSend"
          @click="submitSend"
        >
          보내기
        </v-btn>
      </div>
    </div>
  </v-dialog>

  <v-dialog v-model="resultOpen" max-width="440" persistent>
    <div class="wage-slip-broadcast-dialog wage-slip-broadcast-dialog--result">
      <div class="wage-slip-broadcast-dialog__header">
        <span>발송 결과</span>
        <button
          type="button"
          class="wage-slip-broadcast-dialog__close"
          aria-label="닫기"
          @click="closeResult"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>
      <div class="wage-slip-broadcast-dialog__body">
        <p class="wage-slip-broadcast-dialog__result-summary">{{ resultSummary }}</p>
        <div v-if="resultFailed.length" class="wage-slip-broadcast-dialog__result-block">
          <p class="wage-slip-broadcast-dialog__result-label">발송 실패</p>
          <ul class="wage-slip-broadcast-dialog__result-list">
            <li v-for="(row, idx) in resultFailed" :key="`fail-${idx}`">
              {{ row.displayName }}<span v-if="row.message"> · {{ row.message }}</span>
            </li>
          </ul>
        </div>
        <div v-if="resultUnmatched.length" class="wage-slip-broadcast-dialog__result-block">
          <p class="wage-slip-broadcast-dialog__result-label">앱 미연결로 제외</p>
          <ul class="wage-slip-broadcast-dialog__result-list">
            <li v-for="(row, idx) in resultUnmatched" :key="`un-${idx}`">
              {{ row.displayName }}<span v-if="row.bizGubun"> · {{ row.bizGubun }}</span>
            </li>
          </ul>
        </div>
        <p v-if="resultHasIssue" class="wage-slip-broadcast-dialog__result-guide">
          빠진 사람은 앱 연결 또는 급여 확인 후 다시 발송해 주세요. 같은 달은 다시 보낼 수 있습니다.
        </p>
      </div>
      <div class="wage-slip-broadcast-dialog__footer">
        <v-btn variant="flat" class="btn-save" @click="closeResult">확인</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { wageMagamApi } from '@/services/api';
import { formatYearMonthLabel } from '@/composables/wage/useWageMagam';

type PreviewRow = { displayName: string; bizGubun?: string; reason?: string };
type MatchedRow = PreviewRow & {
  cayIdx: number;
  alreadySent?: boolean;
  sentAt?: string;
};
type ResultRow = { displayName: string; message?: string; bizGubun?: string };
type BroadcastLog = { sentAt?: string; sentCount?: number; sentByName?: string };
type PreviewData = {
  matchedCount: number;
  skippedCount: number;
  unmatchedCount: number;
  matched: MatchedRow[];
  skipped: PreviewRow[];
  unmatched: PreviewRow[];
  isClosed?: boolean;
  isFirstBroadcast?: boolean;
  lastBroadcast?: BroadcastLog | null;
  broadcastHistory?: BroadcastLog[];
};

const formatBroadcastAt = (raw?: string) => {
  const s = String(raw || '').replace('T', ' ');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return s;
  return `${m[1]}년 ${parseInt(m[2], 10)}월 ${parseInt(m[3], 10)}일 ${m[4]}:${m[5]}`;
};

const props = defineProps<{
  isClosed: boolean;
  memberCode: string;
  yearMonth: string;
  bizGubun: string;
}>();

const dialogOpen = ref(false);
const previewLoading = ref(false);
const sending = ref(false);
const previewError = ref('');
const preview = ref<PreviewData | null>(null);
const selectedCayIdxs = ref<number[]>([]);
const resultOpen = ref(false);
const resultSummary = ref('');
const resultFailed = ref<ResultRow[]>([]);
const resultUnmatched = ref<ResultRow[]>([]);

const yearMonthLabel = computed(() => formatYearMonthLabel(props.yearMonth));
const alreadySentCount = computed(
  () => (preview.value?.matched || []).filter((row) => row.alreadySent).length
);
const canSend = computed(() => selectedCayIdxs.value.length > 0 && !!props.isClosed);
const isFirstBroadcast = computed(() => !!preview.value?.isFirstBroadcast);
const lastBroadcast = computed(() => preview.value?.lastBroadcast || null);
const broadcastHistory = computed(() =>
  Array.isArray(preview.value?.broadcastHistory) ? preview.value.broadcastHistory : []
);
const resultHasIssue = computed(
  () => resultFailed.value.length > 0 || resultUnmatched.value.length > 0
);
const historyText = computed(() => {
  if (!preview.value) return `${yearMonthLabel.value} 임금명세서 발송 여부를 확인합니다.`;
  if (preview.value.isFirstBroadcast || !lastBroadcast.value?.sentAt) {
    return `${yearMonthLabel.value} 임금명세서는 처음 보내는 것입니다.`;
  }
  const when = formatBroadcastAt(lastBroadcast.value.sentAt);
  return `${yearMonthLabel.value} 임금명세서는 ${when}에 보낸 적이 있습니다.`;
});

const closeDialog = () => {
  if (sending.value) return;
  dialogOpen.value = false;
};

const loadPreview = async () => {
  previewLoading.value = true;
  previewError.value = '';
  preview.value = null;
  try {
    const res = await wageMagamApi.previewSlipBroadcast({
      mCode: props.memberCode,
      yearMonth: props.yearMonth,
      bizGubun: props.bizGubun,
    });
    if (!res.data?.success) {
      previewError.value = res.data?.message || '대상 확인에 실패했습니다.';
      return;
    }
    const data = res.data.data || {};
    preview.value = {
      matchedCount: Number(data.matchedCount || 0),
      skippedCount: Number(data.skippedCount || 0),
      unmatchedCount: Number(data.unmatchedCount || 0),
      matched: Array.isArray(data.matched) ? data.matched : [],
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
      unmatched: Array.isArray(data.unmatched) ? data.unmatched : [],
      isClosed: !!data.isClosed,
      isFirstBroadcast: !!data.isFirstBroadcast,
      lastBroadcast: data.lastBroadcast || null,
      broadcastHistory: Array.isArray(data.broadcastHistory) ? data.broadcastHistory : [],
    };
    // 아직 못 받은 사람만 기본 체크 — 이미 받은 사람은 해제된 채로 열림
    selectedCayIdxs.value = (preview.value?.matched || [])
      .filter((row) => !row.alreadySent)
      .map((row) => row.cayIdx);
    if (!data.isClosed) {
      previewError.value = '임금 마감 후 발송할 수 있습니다.';
    }
  } catch (error: any) {
    previewError.value =
      error.response?.data?.message || error.message || '대상 확인에 실패했습니다.';
  } finally {
    previewLoading.value = false;
  }
};

const openDialog = () => {
  if (!props.isClosed || sending.value) return;
  if (!props.memberCode || !props.yearMonth || !props.bizGubun) {
    alert('시설·연월·사업구분 정보가 없습니다.');
    return;
  }
  dialogOpen.value = true;
  void loadPreview();
};

const submitSend = async () => {
  if (!canSend.value) return;
  sending.value = true;
  try {
    const res = await wageMagamApi.sendSlipBroadcast({
      mCode: props.memberCode,
      yearMonth: props.yearMonth,
      bizGubun: props.bizGubun,
      title: `${yearMonthLabel.value} 임금명세서`,
      targetCayIdxs: [...selectedCayIdxs.value],
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || '발송 실패');
    }
    const sent = Number(res.data.data?.sent || 0);
    const failed = Number(res.data.data?.failed || 0);
    const results = Array.isArray(res.data.data?.results) ? res.data.data.results : [];
    resultFailed.value = results.filter((r: ResultRow & { ok?: boolean }) => !r.ok);
    resultUnmatched.value = preview.value?.unmatched || [];
    resultSummary.value = failed
      ? `${sent}명 발송 완료 · ${failed}명 실패했습니다.`
      : `${sent}명에게 임금명세서를 발송했습니다.`;
    dialogOpen.value = false;
    resultOpen.value = true;
  } catch (error: any) {
    alert(error.response?.data?.message || error.message || '임금명세서 발송에 실패했습니다.');
  } finally {
    sending.value = false;
  }
};

const closeResult = () => {
  resultOpen.value = false;
  resultFailed.value = [];
  resultUnmatched.value = [];
};
</script>
